import { Account } from '@near-js/accounts';
import { PublicKey } from '@near-js/crypto';
import { BrowserLocalStorageKeyStore } from '@near-js/keystores-browser';
import { JsonRpcProvider } from '@near-js/providers';
import { InMemorySigner, KeyPairSigner } from '@near-js/signers';
import { actionCreators } from '@near-js/transactions';
import { NearConnector } from '@hot-labs/near-connect';
import { toastifyMessage } from '../components/Toastify/Toastify';
import getConfig from '../config';

export const {
  networkId, nodeUrl, contractName, contractMethods, backendApi,
} = getConfig();

const browserKeyStore = new BrowserLocalStorageKeyStore();
const browserSigner = new InMemorySigner(browserKeyStore);
const rpcProvider = new JsonRpcProvider({ url: nodeUrl });
let connectorPromise;
let activeAccountIdCache = null;
let connectorListenersAttached = false;
let signInInFlightPromise = null;
let lastWalletMaintenance = null;
let lastTooManyKeysToastAt = 0;
export const TOO_MANY_ACCESS_KEYS_MESSAGE = 'Account has too many access keys. Please cleanup old wallet keys and try again.';
const METEOR_WALLET_ID = 'meteor-wallet';
const METEOR_WALLET_MANIFEST = {
  version: '1.1.0',
  wallets: [
    {
      id: METEOR_WALLET_ID,
      name: 'Meteor Wallet',
      icon: 'https://raw.githubusercontent.com/Meteor-Wallet/meteor_wallet_sdk/main/assets/meteor-logo-svg.svg',
      description: 'The most simple and secure wallet to manage your crypto, access DeFi, and explore Web3',
      website: 'https://meteorwallet.app/',
      version: '1.1.0',
      executor: 'https://raw.githubusercontent.com/Meteor-Wallet/meteor_wallet_sdk/data-storage/storage/meteor-near-connect-latest.js',
      type: 'sandbox',
      features: {
        signMessage: true,
        signInWithoutAddKey: true,
        signInAndSignMessage: true,
        signAndSendTransaction: true,
        signAndSendTransactions: true,
        signInWithFunctionCallKey: true,
        signDelegateActions: true,
        mainnet: true,
        testnet: true,
      },
      permissions: {
        storage: true,
        allowsOpen: [
          'https://chromewebstore.google.com',
          'https://wallet.meteorwallet.app',
          'https://meteorwallet.app',
        ],
        external: ['meteorCom', 'meteorComV2'],
      },
      platform: {
        web: 'https://wallet.meteorwallet.app',
        chrome: 'https://chromewebstore.google.com/detail/meteor-wallet/pcndjhkinnkaohffealmlmhaepkpmgkb',
      },
    },
  ],
};

const ACCESS_KEYS_PAGE_LIMIT = 50;
const ACCESS_KEYS_WARNING_THRESHOLD = 80;
const SESSION_KEY_STORAGE_KEY = 'ofp:near-connect-session-key-by-account';

const readSessionKeyByAccount = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(SESSION_KEY_STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeSessionKeyByAccount = (value) => {
  try {
    localStorage.setItem(SESSION_KEY_STORAGE_KEY, JSON.stringify(value || {}));
  } catch (error) {
    // ignore storage errors
  }
};

const rememberSessionKeyByAccount = (accountId, publicKeyRaw) => {
  const publicKey = normalizePublicKey(publicKeyRaw || '');
  if (!accountId || !publicKey) return;
  const current = readSessionKeyByAccount();
  current[accountId] = publicKey;
  writeSessionKeyByAccount(current);
};

const rememberSessionKeys = (accounts = []) => {
  if (!Array.isArray(accounts) || !accounts.length) return;
  accounts.forEach((item) => {
    rememberSessionKeyByAccount(
      item?.accountId,
      item?.publicKey || item?.public_key || '',
    );
  });
};

const getRememberedSessionKey = (accountId) => {
  if (!accountId) return '';
  const current = readSessionKeyByAccount();
  return current[accountId] || '';
};

const forgetSessionKey = (accountId) => {
  if (!accountId) return;
  const current = readSessionKeyByAccount();
  if (!current[accountId]) return;
  delete current[accountId];
  writeSessionKeyByAccount(current);
};

const getErrorMessage = (error) => {
  const toStringValue = typeof error?.toString === 'function' ? error.toString() : '';
  return [
    error?.message,
    error?.cause?.message,
    error?.error?.message,
    error?.status?.Failure,
    toStringValue !== '[object Object]' ? toStringValue : '',
  ].filter(Boolean).join(' | ');
};

const resolveSessionKeyFromLocalStorage = (accountId) => {
  if (!accountId) return '';
  try {
    const keyRegex = /ed25519:[1-9A-HJ-NP-Za-km-z]{30,}/g;
    const candidates = [];
    const storageKeys = Object.keys(localStorage || {});
    storageKeys.forEach((storageKey) => {
      const value = localStorage.getItem(storageKey) || '';
      const source = `${storageKey}\n${value}`;
      const hasAccountHint = source.includes(accountId)
        || storageKey.includes('near-api-js:keystore')
        || storageKey.includes('meteor')
        || storageKey.includes('wallet');
      if (!hasAccountHint) return;
      const matches = source.match(keyRegex) || [];
      matches.forEach((match) => {
        const normalized = normalizePublicKey(match);
        if (normalized && !candidates.includes(normalized)) {
          candidates.push(normalized);
        }
      });
    });
    return candidates[0] || '';
  } catch (error) {
    return '';
  }
};

export const isTooManyAccessKeysError = (error) => {
  const text = getErrorMessage(error).toLowerCase();
  return text.includes('more than')
    && text.includes('access key')
    && text.includes('paginated view_access_key_list request');
};

const isIframeNotLoadedError = (error) => getErrorMessage(error).toLowerCase().includes('iframe not loaded');

const notifyTooManyKeysError = () => {
  const now = Date.now();
  if (now - lastTooManyKeysToastAt < 5000) return;
  lastTooManyKeysToastAt = now;
  toastifyMessage(TOO_MANY_ACCESS_KEYS_MESSAGE);
};

const hasLocalSigningKey = async (accountLike, accountId) => {
  if (!accountLike?.connection?.signer || !accountId) return false;
  try {
    const publicKey = await accountLike.connection.signer.getPublicKey(accountId, networkId);
    return !!publicKey;
  } catch (error) {
    return false;
  }
};

export const normalizeNearTxError = (error) => {
  if (!error) return error;
  if (isTooManyAccessKeysError(error)) {
    return {
      ...error,
      code: error?.code || -32000,
      type: 'TOO_MANY_ACCESS_KEYS',
      isTooManyAccessKeys: true,
      userMessage: TOO_MANY_ACCESS_KEYS_MESSAGE,
    };
  }
  return error;
};

export const closeWalletSelectorModal = () => {
  // near-connect popups are self-managed
};

const getNearConnection = async () => ({
  connection: {
    networkId,
    provider: rpcProvider,
    signer: browserSigner,
  },
  account: async (accountId) => {
    const keyPair = await browserKeyStore.getKey(networkId, accountId);
    const signer = keyPair ? new KeyPairSigner(keyPair) : undefined;
    const account = new Account(accountId, rpcProvider, signer);
    // Compatibility shim for legacy helper functions that access account.connection.signer.
    account.connection = {
      networkId,
      provider: rpcProvider,
      signer: browserSigner,
    };
    account.inMemorySigner = browserSigner;
    return account;
  },
});

const initSelector = async () => {
  if (!connectorPromise) {
    connectorPromise = Promise.resolve(new NearConnector({
      network: networkId,
      providers: {
        [networkId]: [nodeUrl],
      },
      manifest: METEOR_WALLET_MANIFEST,
      features: {
        signAndSendTransaction: true,
        signMessage: true,
      },
    }));
  }
  const selector = await connectorPromise;
  if (!connectorListenersAttached) {
    selector.on('wallet:signIn', ({ accounts }) => {
      activeAccountIdCache = accounts?.[0]?.accountId || null;
      rememberSessionKeys(accounts || []);
    });
    selector.on('wallet:signOut', () => {
      activeAccountIdCache = null;
    });
    connectorListenersAttached = true;
  }
  if (!activeAccountIdCache) {
    try {
      const connected = await selector.getConnectedWallet();
      activeAccountIdCache = connected?.accounts?.[0]?.accountId || null;
      rememberSessionKeys(connected?.accounts || []);
    } catch (error) {
      activeAccountIdCache = null;
    }
  }
  return { selector, modal: null };
};

const getActiveAccountId = () => activeAccountIdCache;

const getSelectedWalletSafe = async (selector) => {
  try {
    return await selector.wallet();
  } catch (error) {
    if (
      error?.message?.includes('No wallet selected')
      || error?.message?.includes('No accounts found')
    ) {
      return null;
    }
    throw error;
  }
};

const getConnectedAccountIdSafe = async (selector) => {
  try {
    const connected = await selector.getConnectedWallet();
    return connected?.accounts?.[0]?.accountId || null;
  } catch (error) {
    return null;
  }
};

const resolveLogoutAccountId = async (selector) => {
  // Prefer wallet-reported account over local cache to avoid stale accountId
  // when users switch between multiple accounts in wallet UI.
  const connectedAccountId = await getConnectedAccountIdSafe(selector);
  return connectedAccountId || getActiveAccountId() || null;
};

const getKeyNonce = (keyInfo) => Number(keyInfo?.access_key?.nonce || 0);

const resolveLikelySessionKeyFromChain = async (accountId) => {
  if (!accountId) return '';
  try {
    const allKeys = await listAccessKeysPaginated(accountId);
    const dappKeys = getDappFunctionCallKeys(allKeys);
    const functionCallKeys = getAllFunctionCallKeys(allKeys);
    const byMaxNonce = (keys = []) => keys.reduce((best, current) => (
      getKeyNonce(current) > getKeyNonce(best) ? current : best
    ), keys[0]);

    // Never fall back to a full-access key. That is the key Meteor uses to sign.
    const bestDappKey = dappKeys.length ? byMaxNonce(dappKeys) : null;
    const bestFunctionCallKey = functionCallKeys.length ? byMaxNonce(functionCallKeys) : null;
    const bestKey = bestDappKey || bestFunctionCallKey;
    const resolvedKey = normalizePublicKey(bestKey?.public_key || bestKey?.publicKey || '');
    return resolvedKey;
  } catch (error) {
    return '';
  }
};

export const isWalletCancelError = (error) => {
  const stringifySafe = (value) => {
    try {
      return JSON.stringify(value || {});
    } catch (e) {
      return '';
    }
  };

  const toStringValue = typeof error?.toString === 'function' ? error.toString() : '';
  const raw = [
    error?.message,
    error?.cause?.message,
    error?.error?.message,
    error?.status?.Failure,
    toStringValue !== '[object Object]' ? toStringValue : '',
    stringifySafe(error),
  ]
    .filter(Boolean)
    .join(' | ')
    .toLowerCase();

  return raw.includes('user closed the window')
    || raw.includes('user rejected')
    || raw.includes('request canceled')
    || raw.includes('request cancelled')
    || raw.includes('rejected by user')
    || raw.includes('cancelled by user')
    || raw.includes('canceled by user')
    || raw.includes('meteoractionerror');
};

const waitForActiveAccount = async (timeoutMs = 15000, intervalMs = 100, expectedAccountId = '') => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    const currentAccountId = getActiveAccountId();
    if (currentAccountId && (!expectedAccountId || currentAccountId === expectedAccountId)) {
      return true;
    }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  const currentAccountId = getActiveAccountId();
  return !!(currentAccountId && (!expectedAccountId || currentAccountId === expectedAccountId));
};

const normalizeFunctionCall = (input) => {
  if (input.length === 1 && typeof input[0] === 'object' && input[0] !== null) {
    return input[0];
  }
  const [contractId, methodName, args = {}, gas, attachedDeposit] = input;
  return {
    contractId,
    methodName,
    args,
    gas,
    attachedDeposit,
  };
};

const createWalletSelectorAccount = async (near, selector, accountId) => {
  const account = await near.account(accountId);
  const nativeFunctionCall = account.functionCall.bind(account);
  account.functionCall = async (...input) => {
    const {
      contractId,
      methodName,
      args = {},
      gas,
      attachedDeposit,
      deposit,
    } = normalizeFunctionCall(input);
    const wallet = await getSelectedWalletSafe(selector);
    if (!wallet) {
      throw new Error('No wallet selected');
    }
    try {
      return await wallet.signAndSendTransaction({
        network: networkId,
        signerId: accountId,
        receiverId: contractId,
        actions: [
          actionCreators.functionCall(
            methodName,
            args,
            BigInt(gas || '30000000000000'),
            BigInt(attachedDeposit || deposit || '0'),
          ),
        ],
      });
    } catch (error) {
      if (isIframeNotLoadedError(error)) {
        const refreshedWallet = await getSelectedWalletSafe(selector);
        if (refreshedWallet?.signAndSendTransaction) {
          return refreshedWallet.signAndSendTransaction({
            network: networkId,
            signerId: accountId,
            receiverId: contractId,
            actions: [
              actionCreators.functionCall(
                methodName,
                args,
                BigInt(gas || '30000000000000'),
                BigInt(attachedDeposit || deposit || '0'),
              ),
            ],
          });
        }
      }
      if (!isTooManyAccessKeysError(error)) {
        throw normalizeNearTxError(error);
      }
      notifyTooManyKeysError();
      // Fallback requires a local signer key; wallet-selector sessions may not store one in browser keystore.
      const canUseNativeFallback = await hasLocalSigningKey(account, accountId);
      if (!canUseNativeFallback) {
        throw normalizeNearTxError(error);
      }
      try {
        // Fallback path bypasses wallet's internal access-key listing logic.
        return await nativeFunctionCall(
          contractId,
          methodName,
          args,
          gas || '30000000000000',
          attachedDeposit || deposit || '0',
        );
      } catch (fallbackError) {
        notifyTooManyKeysError();
        throw normalizeNearTxError(fallbackError);
      }
    }
  };
  return account;
};

const LOGIN_MESSAGE = 'Login to Open Forest Protocol';
let pendingLoginPayload = null;

const fetchLoginChallenge = async () => {
  const challengeResponse = await fetch(`${backendApi}/api/dashboard/auth/challenge`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!challengeResponse.ok) return null;
  const challengeData = await challengeResponse.json();
  const nonce = challengeData?.nonce
    || challengeData?.challenge
    || challengeData?.data?.nonce
    || challengeData?.result?.nonce
    || '';
  if (!nonce || typeof nonce !== 'string') return null;
  const nonceBytes = Uint8Array.from(Buffer.from(nonce, 'base64'));
  if (!nonceBytes.length) return null;
  const recipient = window.location.hostname;
  return {
    message: LOGIN_MESSAGE,
    nonce,
    recipient,
    signMessageParams: {
      message: LOGIN_MESSAGE,
      recipient,
      nonce: nonceBytes,
    },
  };
};

const toLoginPayload = (signedAccount, challenge) => {
  const signedMessage = signedAccount?.signedMessage || signedAccount?.signed_message || {};
  const signature = signedMessage?.signature
    || signedMessage?.signatura
    || signedAccount?.signature
    || signedAccount?.signatura
    || '';
  const publicKey = normalizePublicKey(
    signedMessage?.publicKey
    || signedMessage?.public_key
    || signedAccount?.publicKey
    || signedAccount?.public_key
    || '',
  );
  const accountId = signedAccount?.accountId || signedMessage?.accountId || '';
  if (!accountId || !signature || !publicKey || !challenge?.nonce) return null;
  return {
    message: challenge.message,
    nonce: challenge.nonce,
    recipient: challenge.recipient,
    account_id: accountId,
    public_key: publicKey,
    hash: signature,
  };
};

const takePendingLoginPayload = (accountId) => {
  if (!pendingLoginPayload) return null;
  if (accountId && pendingLoginPayload.account_id !== accountId) {
    pendingLoginPayload = null;
    return null;
  }
  const value = pendingLoginPayload;
  pendingLoginPayload = null;
  return value;
};

const requestWalletSignIn = async (selector, options = {}) => {
  const { forceConnect = false, walletId, withLoginMessage = false } = options;
  try {
    if (!forceConnect && !withLoginMessage && getActiveAccountId()) return true;

    // Nonce must be ready before the popup opens, so Meteor gets the message
    // in the same connect instead of a second signMessage window.
    const loginChallenge = withLoginMessage ? await fetchLoginChallenge() : null;
    if (withLoginMessage && !loginChallenge) return false;

    if (forceConnect) {
      activeAccountIdCache = null;
      pendingLoginPayload = null;
      await selector.disconnect().catch(() => {});
    }

    let signedAccount = null;
    const onSignedMessage = (event) => {
      signedAccount = event?.accounts?.[0] || null;
    };
    if (loginChallenge) selector.on('wallet:signInAndSignMessage', onSignedMessage);
    try {
      await selector.connect({
        walletId: walletId || METEOR_WALLET_ID,
        ...(loginChallenge ? { signMessageParams: loginChallenge.signMessageParams } : {}),
      });
    } finally {
      if (loginChallenge) selector.off('wallet:signInAndSignMessage', onSignedMessage);
    }
    const connectedWallet = await selector.getConnectedWallet();
    const connectedAccountId = connectedWallet?.accounts?.[0]?.accountId || null;
    activeAccountIdCache = connectedAccountId;
    rememberSessionKeys(connectedWallet?.accounts || []);
    if (connectedAccountId) {
      let resolvedSessionKey = await resolveCurrentSessionPublicKey(selector, connectedAccountId);
      if (!resolvedSessionKey) {
        resolvedSessionKey = await resolveLikelySessionKeyFromChain(connectedAccountId);
      }
      if (resolvedSessionKey) {
        rememberSessionKeyByAccount(connectedAccountId, resolvedSessionKey);
      }
    }
    if (loginChallenge) {
      const payload = toLoginPayload(signedAccount, loginChallenge);
      if (!payload) {
        console.log('walletSignInMissingSignature', signedAccount);
        return false;
      }
      pendingLoginPayload = payload;
      activeAccountIdCache = payload.account_id;
    }
    const connected = await waitForActiveAccount(15000, 100, activeAccountIdCache || connectedAccountId || '');
    if (connected) closeWalletSelectorModal();
    return connected;
  } catch (error) {
    closeWalletSelectorModal();
    if (isWalletCancelError(error)) {
      return !!getActiveAccountId();
    }
    console.log('walletSignInError', error);
    return false;
  }
};

export const formatAccountId = (accountId, len = 16) => {
  if (accountId.length > len) {
    return `${accountId.substr(0, len - 3)}...`;
  }
  return accountId;
};

export const getContract = (account, methods = contractMethods, contractId) => {
  const targetContractId = contractName[contractId];
  const changeMethods = methods?.changeMethods || [];
  const viewMethods = methods?.viewMethods || [];

  const contract = {};

  changeMethods.forEach((methodName) => {
    contract[methodName] = async (args = {}, gas = '30000000000000', deposit = '0') => account.functionCall(
      targetContractId,
      methodName,
      args,
      gas,
      deposit,
    );
  });

  viewMethods.forEach((methodName) => {
    contract[methodName] = async (args = {}) => {
      try {
        // near-api-js >= 6 expects an options object.
        return await account.viewFunction({
          contractId: targetContractId,
          methodName,
          args,
        });
      } catch (error) {
        // near-api-js <= 0.38 positional fallback.
        return account.viewFunction(
          targetContractId,
          methodName,
          args,
        );
      }
    };
  });

  return contract;
};

export const getWallet = async () => {
  const near = await getNearConnection();
  const { selector } = await initSelector();
  const wallet = {
    requestSignIn: async (options = {}) => {
      const { forceConnect = false, walletId, withLoginMessage = false } = options;
      if (!forceConnect && !withLoginMessage && getActiveAccountId()) return true;
      if (signInInFlightPromise && !forceConnect) {
        if (getActiveAccountId()) {
          return signInInFlightPromise;
        }
        signInInFlightPromise = null;
      } else if (forceConnect) {
        signInInFlightPromise = null;
      }
      signInInFlightPromise = (async () => {
        try {
          return await requestWalletSignIn(selector, {
            forceConnect,
            walletId: walletId || METEOR_WALLET_ID,
            withLoginMessage,
          });
        } finally {
          signInInFlightPromise = null;
        }
      })();
      try {
        return await signInInFlightPromise;
      } catch (error) {
        closeWalletSelectorModal();
        if (isWalletCancelError(error)) {
          return !!getActiveAccountId();
        }
        console.log('walletRequestSignInError', error);
        return false;
      }
    },
    signIn: async (options = {}) => {
      return wallet.requestSignIn(options);
    },
    signOut: async () => {
      try {
        const accountId = await resolveLogoutAccountId(selector);
        if (!accountId) return true;
        pendingLoginPayload = null;
        activeAccountIdCache = accountId;
        const maintenanceResult = await tryDeleteCurrentSessionKey(selector, accountId, near);
        lastWalletMaintenance = maintenanceResult;
        const deleteStepOk = !maintenanceResult?.warning;
        if (!deleteStepOk) {
          return false;
        }
        // Silent logout mode: once the key is deleted, local app session is cleared
        // without triggering an extra wallet signOut confirmation popup.
        activeAccountIdCache = null;
        closeWalletSelectorModal();
        return true;
      } catch (error) {
        if (isWalletCancelError(error)) {
          return false;
        }
        lastWalletMaintenance = {
          ...(lastWalletMaintenance || {}),
          warning: `Wallet signOut failed: ${getErrorMessage(error)}`,
        };
        console.log('walletSignOutError', error);
        closeWalletSelectorModal();
        return false;
      }
    },
    changeWallet: async () => {
      try {
        if (getActiveAccountId()) {
          await selector.disconnect().catch(() => {});
        }
        await selector.connect({ walletId: METEOR_WALLET_ID });
        const connectedWallet = await selector.getConnectedWallet();
        activeAccountIdCache = connectedWallet?.accounts?.[0]?.accountId || null;
      } catch (error) {
        console.log('walletChangeError', error);
      }
    },
    isSignedIn: () => !!getActiveAccountId(),
    getActiveAccountId: () => getActiveAccountId(),
    getAccessKeysHealth: async (accountId) => {
      const targetAccountId = accountId || getActiveAccountId();
      return getAccessKeysHealth(targetAccountId);
    },
    getLastMaintenance: () => lastWalletMaintenance,
    cleanupFunctionCallKeys: async (options = {}) => {
      const accountId = getActiveAccountId();
      if (!accountId) {
        return {
          accountId: '',
          totalKeys: 0,
          dappKeysCount: 0,
          functionCallKeysCount: 0,
          deletedKeys: 0,
          warning: 'No active account for cleanup.',
        };
      }
      const result = await tryDeleteFunctionCallKeys(selector, accountId, {
        scope: options?.scope || 'all',
        batchSize: options?.batchSize || 10,
        near,
      });
      lastWalletMaintenance = result;
      if (result.warning) {
        console.log('walletDeleteKeysWarning', result.warning);
      }
      return result;
    },
    account: async () => {
      const connectedAccountId = await getConnectedAccountIdSafe(selector);
      const accountId = connectedAccountId || getActiveAccountId();
      if (!accountId) return null;
      activeAccountIdCache = accountId;
      return createWalletSelectorAccount(near, selector, accountId);
    },
    onAccountChange: (handler = () => {}) => {
      const onSignIn = async ({ accounts }) => {
        activeAccountIdCache = accounts?.[0]?.accountId || null;
        const accountId = getActiveAccountId();
        const account = accountId ? await createWalletSelectorAccount(near, selector, accountId) : null;
        handler(account);
      };
      const onSignOut = () => {
        activeAccountIdCache = null;
        handler(null);
      };
      selector.on('wallet:signIn', onSignIn);
      selector.on('wallet:signOut', onSignOut);
      return () => {
        selector.off('wallet:signIn', onSignIn);
        selector.off('wallet:signOut', onSignOut);
      };
    },
  };
  const contractAccount = await near.account(contractName[0]);
  return { near, wallet, contractAccount };
};

const normalizeTxResult = (txResult) => (Array.isArray(txResult) ? txResult[0] : txResult);

const extractMethodName = (actions = []) => actions
  .map((action) => action?.FunctionCall?.methodName || action?.FunctionCall?.method_name)
  .find(Boolean) || null;

const normalizePublicKey = (publicKey) => {
  if (!publicKey || typeof publicKey !== 'string') return '';
  return publicKey.startsWith('ed25519:') ? publicKey : `ed25519:${publicKey}`;
};

const readAccessKeysPage = async (accountId, afterKey = null, limit = ACCESS_KEYS_PAGE_LIMIT) => {
  const response = await fetch(nodeUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 'view-access-key-list',
      method: 'query',
      params: {
        request_type: 'view_access_key_list',
        finality: 'final',
        account_id: accountId,
        after_key: afterKey,
        limit,
      },
    }),
  });
  const data = await response.json();
  if (data?.error) {
    throw new Error(data?.error?.message || 'Failed to read account access keys');
  }
  return {
    keys: data?.result?.keys || [],
    lastKey: data?.result?.last_key || null,
  };
};

export const listAccessKeysPaginated = async (accountId, limit = ACCESS_KEYS_PAGE_LIMIT) => {
  if (!accountId) return [];
  const keys = [];
  let afterKey = null;
  let safetyCounter = 0;

  do {
    // eslint-disable-next-line no-await-in-loop
    const page = await readAccessKeysPage(accountId, afterKey, limit);
    keys.push(...(page.keys || []));
    afterKey = page.lastKey || null;
    safetyCounter += 1;
  } while (afterKey && safetyCounter < 1000);

  return keys;
};

const getFunctionCallPermission = (keyInfo) => {
  const permission = keyInfo?.access_key?.permission;
  if (!permission || typeof permission === 'string') return null;
  return permission?.FunctionCall || permission?.function_call || null;
};

const contractNameList = Array.isArray(contractName)
  ? contractName
  : [contractName];

const getDappFunctionCallKeys = (keys = []) => keys.filter((keyInfo) => {
  const functionCallPermission = getFunctionCallPermission(keyInfo);
  if (!functionCallPermission) return false;
  const receiverId = functionCallPermission?.receiver_id || functionCallPermission?.receiverId || '';
  return contractNameList.includes(receiverId);
});

const getAllFunctionCallKeys = (keys = []) => keys.filter((keyInfo) => !!getFunctionCallPermission(keyInfo));

export const getAccessKeysHealth = async (accountId) => {
  if (!accountId) {
    return {
      count: 0,
      dappKeysCount: 0,
      warning: '',
      needsCleanup: false,
    };
  }
  try {
    const keys = await listAccessKeysPaginated(accountId);
    const dappKeysCount = getDappFunctionCallKeys(keys).length;
    const count = keys.length;
    const needsCleanup = count > ACCESS_KEYS_WARNING_THRESHOLD;
    return {
      count,
      dappKeysCount,
      warning: needsCleanup ? 'Too many wallet keys. Please cleanup old keys.' : '',
      needsCleanup,
    };
  } catch (error) {
    const tooManyAccessKeys = isTooManyAccessKeysError(error);
    return {
      count: 0,
      dappKeysCount: 0,
      warning: tooManyAccessKeys
        ? 'Need to cleanup old keys. Wallet returned key-limit error.'
        : 'Failed to read wallet keys.',
      needsCleanup: true,
      error: getErrorMessage(error),
    };
  }
};

const sendDeleteKeysWithFallback = async ({
  walletInstance,
  fallbackAccount,
  accountId,
  batchKeys,
}) => {
  const walletActions = batchKeys.map((publicKey) => actionCreators.deleteKey(
    PublicKey.fromString(normalizePublicKey(publicKey)),
  ));
  try {
    return await walletInstance.signAndSendTransaction({
      network: networkId,
      signerId: accountId,
      receiverId: accountId,
      actions: walletActions,
    });
  } catch (error) {
    if (isIframeNotLoadedError(error)) {
      throw new Error('Iframe not loaded');
    }
    if (!isTooManyAccessKeysError(error) || !fallbackAccount) {
      throw error;
    }
    const canUseNativeFallback = await hasLocalSigningKey(fallbackAccount, accountId);
    if (!canUseNativeFallback) {
      throw error;
    }
    const nativeActions = batchKeys.map((publicKey) => actionCreators.deleteKey(
      PublicKey.fromString(normalizePublicKey(publicKey)),
    ));
    try {
      return await fallbackAccount.signAndSendTransaction({
        receiverId: accountId,
        actions: nativeActions,
      });
    } catch (fallbackError) {
      // Backward-compatible signature for older near-api-js versions.
      return fallbackAccount.signAndSendTransaction(accountId, nativeActions);
    }
  }
};

const deleteDappKeysInBatches = async (
  walletInstance,
  fallbackAccount,
  accountId,
  publicKeys,
  batchSize = 10,
) => {
  let deletedCount = 0;
  for (let i = 0; i < publicKeys.length; i += batchSize) {
    const batchKeys = publicKeys.slice(i, i + batchSize);
    try {
      // eslint-disable-next-line no-await-in-loop
      await sendDeleteKeysWithFallback({
        walletInstance,
        fallbackAccount,
        accountId,
        batchKeys,
      });
    } catch (error) {
      if (!isIframeNotLoadedError(error)) {
        throw error;
      }
      // Wallet iframe may be recreated after user closes popup; retry once.
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, 200));
      // eslint-disable-next-line no-await-in-loop
      await sendDeleteKeysWithFallback({
        walletInstance,
        fallbackAccount,
        accountId,
        batchKeys,
      });
    }
    deletedCount += batchKeys.length;
  }
  return deletedCount;
};

const resolveCurrentSessionPublicKey = async (selector, accountId) => {
  try {
    const connectedWallet = await selector.getConnectedWallet();
    const accounts = connectedWallet?.accounts || [];
    const target = accounts.find((item) => item?.accountId === accountId) || accounts[0];
    const publicKey = target?.publicKey || target?.public_key || '';
    if (publicKey) return normalizePublicKey(publicKey);
  } catch (error) {
    // continue with local signer fallback
  }

  try {
    const localPublicKey = await browserSigner.getPublicKey(accountId, networkId);
    if (localPublicKey?.toString) {
      return normalizePublicKey(localPublicKey.toString());
    }
  } catch (error) {
    // ignore and return empty key
  }

  return '';
};

const resolveSavedLoginSessionKey = async (selector, accountId) => {
  const rememberedKey = normalizePublicKey(getRememberedSessionKey(accountId) || '');
  if (rememberedKey) return rememberedKey;

  const localStorageKey = resolveSessionKeyFromLocalStorage(accountId);
  if (localStorageKey) {
    rememberSessionKeyByAccount(accountId, localStorageKey);
    return localStorageKey;
  }

  let sessionPublicKey = await resolveCurrentSessionPublicKey(selector, accountId);
  if (!sessionPublicKey) {
    sessionPublicKey = await resolveLikelySessionKeyFromChain(accountId);
  }
  if (sessionPublicKey) {
    rememberSessionKeyByAccount(accountId, sessionPublicKey);
    return sessionPublicKey;
  }

  return '';
};

const resolveValidatedLogoutKey = async (selector, accountId) => {
  const savedKey = normalizePublicKey(await resolveSavedLoginSessionKey(selector, accountId) || '');
  let allKeys = [];
  try {
    allKeys = await listAccessKeysPaginated(accountId);
  } catch (error) {
    return '';
  }
  const functionCallKeySet = new Set(
    getAllFunctionCallKeys(allKeys)
      .map((item) => normalizePublicKey(item?.public_key || item?.publicKey || ''))
      .filter(Boolean),
  );
  if (!functionCallKeySet.size) {
    if (savedKey) forgetSessionKey(accountId);
    return '';
  }

  if (savedKey && functionCallKeySet.has(savedKey)) return savedKey;
  if (savedKey) forgetSessionKey(accountId);

  const chainKey = await resolveLikelySessionKeyFromChain(accountId);
  if (chainKey && functionCallKeySet.has(chainKey)) {
    rememberSessionKeyByAccount(accountId, chainKey);
    return chainKey;
  }
  return '';
};

const tryDeleteCurrentSessionKey = async (selector, accountId, near) => {
  const result = {
    accountId,
    deletedKeys: 0,
    deletedPublicKey: '',
    warning: '',
  };
  if (!accountId) return result;

  const selectedWallet = await getSelectedWalletSafe(selector);
  if (!selectedWallet) {
    result.warning = 'No selected wallet found for key cleanup.';
    return result;
  }

  let fallbackAccount = null;
  if (near) {
    try {
      fallbackAccount = await near.account(accountId);
    } catch (error) {
      fallbackAccount = null;
    }
  }

  const keyForDelete = await resolveValidatedLogoutKey(selector, accountId);
  // Nothing limited to remove. Logout still finishes; the Meteor full-access key stays.
  if (!keyForDelete) return result;

  try {
    await sendDeleteKeysWithFallback({
      walletInstance: selectedWallet,
      fallbackAccount,
      accountId,
      batchKeys: [keyForDelete],
    });
    result.deletedKeys = 1;
    result.deletedPublicKey = keyForDelete;
    forgetSessionKey(accountId);
  } catch (error) {
    result.warning = `Could not delete saved session key: ${getErrorMessage(error)}`;
  }

  return result;
};

const tryDeleteFunctionCallKeys = async (selector, accountId, options = {}) => {
  const { scope = 'dapp', batchSize = 10, near = null } = options;
  const result = {
    accountId,
    totalKeys: 0,
    dappKeysCount: 0,
    functionCallKeysCount: 0,
    deletedKeys: 0,
    warning: '',
  };
  if (!accountId) return result;

  const selectedWallet = await getSelectedWalletSafe(selector);
  if (!selectedWallet) {
    result.warning = 'No selected wallet found for key cleanup.';
    return result;
  }
  let fallbackAccount = null;
  if (near) {
    try {
      fallbackAccount = await near.account(accountId);
    } catch (error) {
      fallbackAccount = null;
    }
  }

  let targetKeys = [];
  try {
    const allKeys = await listAccessKeysPaginated(accountId);
    result.totalKeys = allKeys.length;
    const functionCallKeys = getAllFunctionCallKeys(allKeys);
    const dappKeys = getDappFunctionCallKeys(allKeys);
    result.functionCallKeysCount = functionCallKeys.length;
    result.dappKeysCount = dappKeys.length;
    targetKeys = scope === 'all' ? functionCallKeys : dappKeys;
    if (!targetKeys.length) return result;
  } catch (error) {
    result.warning = `Could not load keys for cleanup: ${getErrorMessage(error)}`;
    return result;
  }

  const publicKeys = targetKeys
    .map((item) => item?.public_key || item?.publicKey)
    .filter(Boolean);
  if (!publicKeys.length) return result;

  try {
    result.deletedKeys = await deleteDappKeysInBatches(
      selectedWallet,
      fallbackAccount,
      accountId,
      publicKeys,
      batchSize,
    );
  } catch (error) {
    result.warning = `Could not cleanup function-call keys: ${getErrorMessage(error)}`;
  }
  return result;
};

export const getLastWalletMaintenance = () => lastWalletMaintenance;

// const normalizeSignatureToBase64 = (signature) => {
//   if (!signature) return '';
//   if (signature instanceof Uint8Array) {
//     return Buffer.from(signature).toString('base64');
//   }
//   if (typeof signature === 'string') {
//     const sanitized = signature.replace(/-/g, '+').replace(/_/g, '/');
//     const padded = `${sanitized}${'='.repeat((4 - (sanitized.length % 4)) % 4)}`;
//     try {
//       return Buffer.from(padded, 'base64').toString('base64');
//     } catch (e) {
//       return '';
//     }
//   }
//   return '';
// };



export const getTxOutcome = (txResult) => {
  const normalizedTxResult = normalizeTxResult(txResult);
  const status = normalizedTxResult?.status || normalizedTxResult?.final_execution_status;
  const isFailure = !!(status?.Failure || status === 'FAILURE');
  const isSuccess = !!(
    status
    && (
      status.SuccessValue !== undefined
      || status.SuccessReceiptId
      || status === 'FINAL'
      || status === 'EXECUTED'
      || status === 'EXECUTED_OPTIMISTIC'
    )
  ) && !isFailure;

  return {
    methodName: extractMethodName(normalizedTxResult?.transaction?.actions || []),
    isSuccess,
    isFailure,
    txHash: normalizedTxResult?.transaction_outcome?.id || normalizedTxResult?.transaction?.hash || '',
    signerId: normalizedTxResult?.transaction?.signer_id || normalizedTxResult?.transaction?.signerId || '',
    raw: normalizedTxResult,
  };
};

export const getTxInfo = async (txHash, signerId) => {
  if (!signerId || !txHash) return null;
  try {
    const response = await fetch(nodeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 1,
        method: 'tx',
        params: [txHash, signerId],
      }),
    });
    const data = await response.json();
    const actions = data?.result?.transaction?.actions || [];
    const methodName = actions
      .map((action) => action.FunctionCall?.method_name)
      .find(Boolean) || null;
    const status = data?.result?.status;
    const isSuccess = !!(status && (status.SuccessValue !== undefined || status.SuccessReceiptId));
    const isFailure = !!(status && status.Failure);
    return {
      methodName,
      isSuccess: isSuccess && !isFailure,
      isFailure,
      raw: data,
    };
  } catch (err) {
    console.log('txInfoFetchError', err);
    return null;
  }
};

export const getSignature = async (account) => {
  const { accountId } = account;
  const block = await account.connection.provider.block({ finality: 'final' });
  const blockNumber = block.header.height.toString();
  const signer = account.inMemorySigner || account.connection.signer;
  const signed = await signer.signMessage(Buffer.from(blockNumber), accountId, networkId);
  const blockNumberSignature = Buffer.from(signed.signature).toString('base64');
  return { blockNumber, blockNumberSignature };
};

export const getAccountPublicKey = async (account) => {
  if (!account) return '';
  try {
    const firstPage = await readAccessKeysPage(account.accountId, null, 1);
    return firstPage?.keys?.[0]?.public_key || '';
  } catch (e) {
    if (isTooManyAccessKeysError(e)) {
      console.log('walletAccessKeyLimitReached', getErrorMessage(e));
    }
    return '';
  }
};

export const buildLoginPayload = async (account) => {
  const accountId = account?.accountId;
  if (!accountId) return null;

  const pending = takePendingLoginPayload(accountId);
  if (pending) return pending;

  try {
    // Session restore and registration still need a signature, but in the same
    // connect popup. A follow-up signMessage opens the stuck Meteor window.
    const { selector } = await initSelector();
    const connected = await requestWalletSignIn(selector, { withLoginMessage: true });
    if (!connected) return null;
    return takePendingLoginPayload(accountId);
  } catch (e) {
    console.log('buildLoginPayloadError', e);
    return null;
  }
};

// eslint-disable-next-line no-return-await
export const postSignedJson = async ({ account, contractNameCurrent, url, data = {} }) => await fetch(url, {
  method: 'POST',
  headers: new Headers({ 'content-type': 'application/json' }),
  body: JSON.stringify({
    ...data,
    accountId: account.accountId,
    contractName: contractNameCurrent,
    ...(await getSignature(account)),
  }),
}).then((res) => res.json());

// eslint-disable-next-line no-return-await
export const postJson = async ({ url, data = {} }) => await fetch(url, {
  method: 'POST',
  headers: new Headers({ 'content-type': 'application/json' }),
  body: JSON.stringify({ ...data }),
}).then((res) => res.json());
